import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, X, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { useChat, getSiteConfig, getAgentIdForPersona, ChatMessage } from './ChatProvider';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { ScrollArea } from '../components/scroll-area';
import { Avatar, AvatarFallback } from '../components/avatar';

// Lumi avatar icon (matches button icon style)
function LumiAvatarIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M7 4v16h10v-3H10V4H7z" fill="currentColor" />
    </svg>
  );
}

// Typing indicator with animated dots
function TypingIndicator() {
  return (
    <div className="flex gap-1 items-center px-1">
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

function ChatMessageItem({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  
  return (
    <div
      className={cn(
        'flex gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {!isUser && (
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="bg-linear-to-br from-primary to-accent text-white text-sm">
            <LumiAvatarIcon className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}
      <div
        className={cn(
          'rounded-2xl px-4 py-2.5 max-w-[85%] text-sm',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-muted text-foreground rounded-bl-md',
          message.isStreaming && 'animate-pulse'
        )}
      >
        <p className="whitespace-pre-wrap wrap-break-word leading-relaxed">
          {message.content || (message.isStreaming ? <TypingIndicator /> : '')}
        </p>
      </div>
    </div>
  );
}

function SuggestionChip({ 
  text, 
  onClick 
}: { 
  text: string; 
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-xs font-medium',
        'bg-secondary/80 text-secondary-foreground',
        'border border-border/50',
        'hover:bg-secondary hover:border-primary/30',
        'transition-all duration-200',
        'animate-in fade-in slide-in-from-bottom-1'
      )}
    >
      {text}
    </button>
  );
}

export function ChatPanel() {
  const { 
    mode,
    isOpen, 
    setIsOpen, 
    messages, 
    isLoading, 
    setIsLoading,
    persona, 
    clearMessages,
    addMessage,
    updateMessage,
    chatApiUrl,
    supabaseAnonKey,
    threadId,
    setThreadId,
  } = useChat();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get site-aware configuration for Lumi
  const siteConfig = getSiteConfig(mode);
  const showWelcome = messages.length === 0;

  // Send message to API
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    // Add user message
    addMessage({ role: 'user', content });
    
    // Add placeholder for assistant response
    const assistantId = addMessage({ 
      role: 'assistant', 
      content: '', 
      isStreaming: true 
    });
    
    setIsLoading(true);

    // Determine SITE_CONTEXT for Lumi's system prompt
    const getSiteContext = (m: string, path: string) => {
      if (path.startsWith('/docs')) return 'docs';
      if (path.startsWith('/build') || path.startsWith('/builder')) return 'builder';
      if (m === 'ai-amp' || m === 'creator' || m === 'brand') return 'app';
      return 'marketing';
    };

    // Generate or reuse thread ID
    const currentThreadId = threadId || crypto.randomUUID();
    if (!threadId) {
      setThreadId(currentThreadId);
    }

    try {
      // Build headers - include Authorization if we have an anon key
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (supabaseAnonKey) {
        headers['Authorization'] = `Bearer ${supabaseAnonKey}`;
        headers['apikey'] = supabaseAnonKey;
      }

      const response = await fetch(chatApiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          thread_id: currentThreadId,
          messages: [
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content },
          ],
          action: {
            type: 'agent.run',
            payload: {
              agent_id: getAgentIdForPersona(persona),
              metadata: { 
                persona,
                SITE_CONTEXT: getSiteContext(mode, window.location.pathname),
                ROUTE: window.location.pathname,
                PAGE_NAME: document.title,
              },
            },
          },
        }),
      });

      if (!response.ok) throw new Error('Chat request failed');

      const data = await response.json();
      updateMessage(assistantId, data.message || data.content || data.data?.response || 'Sorry, I could not process that.');
    } catch (error) {
      console.error('Chat error:', error);
      updateMessage(assistantId, 'Sorry, something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [messages, mode, persona, chatApiUrl, supabaseAnonKey, isLoading, addMessage, updateMessage, setIsLoading, threadId, setThreadId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    sendMessage(input.trim());
    setInput('');
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  if (!isOpen) return null;

  return (
    <div 
      className={cn(
        'fixed top-0 right-0 bottom-0 z-50',
        'w-[420px] max-w-full',
        'flex flex-col',
        'bg-background/95 backdrop-blur-2xl',
        'border-l border-border/50',
        'shadow-2xl shadow-primary/10',
        'animate-in fade-in slide-in-from-right duration-500 ease-in-out'
      )}
      role="dialog"
      aria-label="Chat assistant"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-linear-to-br from-primary to-accent text-white">
                <LumiAvatarIcon className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <span 
              className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-background"
              aria-hidden="true"
            />
          </div>
          <div>
            <h2 className="font-semibold text-sm">Lumi</h2>
            <p className="text-xs text-muted-foreground">Your Ai assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={clearMessages}
              aria-label="Clear chat"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4" ref={scrollRef}>
        <div className="py-4 space-y-4">
          {showWelcome ? (
            <div className="space-y-4 animate-in fade-in duration-500">
              {/* Welcome message */}
              <div className="flex gap-3">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-linear-to-br from-primary to-accent text-white text-sm">
                    <LumiAvatarIcon className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[85%]">
                  <p className="text-sm leading-relaxed">{siteConfig.greeting}</p>
                </div>
              </div>
              
              {/* Suggestions with staggered animation */}
              <div className="pl-11">
                <p className="text-xs text-muted-foreground mb-2">Try asking:</p>
                <div className="flex flex-wrap gap-2">
                  {siteConfig.suggestions.map((suggestion, i) => (
                    <SuggestionChip
                      key={i}
                      text={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <ChatMessageItem key={message.id} message={message} />
            ))
          )}
          
          {/* Loading indicator */}
          {isLoading && !messages[messages.length - 1]?.isStreaming && (
            <div className="flex gap-3 animate-in fade-in duration-200">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="bg-linear-to-br from-primary to-accent text-white text-sm">
                  <LumiAvatarIcon className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                <TypingIndicator />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <form 
        onSubmit={handleSubmit} 
        className="p-4 border-t border-border/50"
      >
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 bg-muted/50 border-0 focus-visible:ring-1"
            disabled={isLoading}
            aria-label="Chat message"
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!input.trim() || isLoading}
            className="shrink-0"
            aria-label={isLoading ? 'Sending...' : 'Send message'}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          Lumi by lykbl • Illuminate your path
        </p>
      </form>
    </div>
  );
}

export default ChatPanel;

